import { combineQuantities, getPriceQueryKey } from "../util/price-extraction";
import {
  BasePriceExtractionConfig,
  LowestPriceQuery,
  PriceQuery,
  ProductPriceDetailsStore,
} from "./productPriceDetails.types";

import { create } from "zustand";
import { PageId } from "../types/pageId";

// function to transform price query to lowest price query
const transformToLowestPriceQuery = (priceQuery: PriceQuery, pageId: PageId): LowestPriceQuery => {
  const { quantity, cacheableQuantities, ...rest } = priceQuery;
  const combinedQuantities = combineQuantities(cacheableQuantities, quantity);
  return {
    ...rest,
    quantity: combinedQuantities,
    pageId,
  };
};

// Zustand store creation
const useProductStore = create<ProductPriceDetailsStore>((set, get) => ({
  productPriceDetails: {},
  priceQueryMap: {},
  priceQueriesLoading: {},
  isError: false,
  featureFlags: {},
  setPricingContext: (pricingContext: string) => {
    set({ pricingContext: { value: pricingContext, isInitialized: true } });
  },
  setVatIncl: (vatIncl: boolean) => {
    set({ vatIncl: vatIncl });
  },
  setBasePriceExtractionConfig: (baseConfig: BasePriceExtractionConfig) => {
    set({
      basePriceExtractionConfig: { ...baseConfig },
    });
  },
  setFeatureFlag: (key, value) =>
    set((state) => ({
      featureFlags: {
        ...state.featureFlags,
        [key]: value,
      },
    })),
  addPriceQuery: async (priceQuery: PriceQuery, pageId: PageId): Promise<void> => {
    const { mpvId, productKey, version, quantity, cacheableQuantities, selections } = priceQuery;
    if (!mpvId && !(productKey && version)) return;

    const priceQueryId = getPriceQueryKey({ mpvId, productKey, version, quantity, cacheableQuantities, selections });

    // If data for this id is already fetched, dont add to mpvIdList
    if (get().productPriceDetails[priceQueryId] || get().priceQueriesLoading[priceQueryId]) return;
    priceQuery.id = priceQueryId;

    const lowestPriceQuery: LowestPriceQuery = transformToLowestPriceQuery(priceQuery, pageId);

    // requestedDisplayStrategy was added because of gallery, we will get this from backend for now
    // priceQuery.requestedDisplayStrategy =
    // priceQuery.requestedDisplayStrategy || PricePresentationType.QtyStartingAtPrice;

    set((state: { priceQueryMap: Record<string, LowestPriceQuery> }) => ({
      priceQueryMap: { ...state.priceQueryMap, [priceQueryId]: lowestPriceQuery },
    }));
  },
}));

export { useProductStore };
